import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TabList,
  Tab,
  Button,
  makeStyles,
  shorthands,
  tokens,
  Tooltip,
  Link,
  Avatar,
} from '@fluentui/react-components';
import {
  Add24Regular,
  Delete24Regular,
  Copy16Regular,
  PersonAccounts20Regular,
  ChevronRight16Regular,
  Person20Filled,
  PersonCall20Filled,
  Building20Filled,
} from '@fluentui/react-icons';
import { ConfirmationDialog } from './ConfirmationDialog';
import Header from './Header';
import { useCustomerForm } from '../hooks/useCustomerForm';
import {
  GeneralSection,
  AddressSection,
  ContactsSection,
  TagsSection,
  SaveStatusBadge,
} from './customerForm';
import OrdersPanel from './OrdersPanel';
import CustomerLocationsPanel from './CustomerLocationsPanel';
import CustomerPaymentMethodsPanel from './CustomerPaymentMethodsPanel';
import { queryKeys } from '../lib/queryClient';
import { deleteCustomer, CompanyHasContactsError } from '../services/customerService';
import CompanyDeleteConflictModal from './CompanyDeleteConflictModal';
import { CustomerProfileSkeleton } from './CustomerProfileSkeleton';
import { useDebug } from '../contexts/DebugContext';
import { apiFetch } from '../lib/apiFetch';
import { useIsMobile } from '../hooks/useIsMobile';

const useStyles = makeStyles({
  // Single elevated workspace card — mirrors the unification applied to the
  // customers list so the profile reads as one continuous surface.
  workspaceCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    flexGrow: 1,
    // Floor to the viewport so flexGrow can't collapse the card to content
    // height when the nested flex height chain fails to resolve (gray gap
    // below the work surface on scroll). flexShrink:0 prevents re-squeeze.
    minHeight: 'calc(100vh - 64px)',
    flexShrink: 0,
    ...shorthands.margin('8px'),
    ...shorthands.overflow('hidden'),
    // Mobile: full-bleed work surface — drop the margin gutter and rounded
    // corners so the content uses every pixel of width.
    '@media (max-width: 768px)': {
      ...shorthands.margin('0'),
      ...shorthands.borderRadius('0'),
    },
  },
  // Wraps the workspace body below the header. The borderTop is the single
  // divider between the header chrome and the content.
  bodyWithPanel: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    minHeight: 0,
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
  },
  workspaceBody: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
    // Allow the body to shrink below its content's intrinsic width so a wide
    // child (e.g. the tab row on mobile) can't force the whole workspace to
    // expand horizontally — it scrolls within its own container instead.
    minWidth: 0,
    ...shorthands.padding('16px'),
    ...shorthands.gap('24px'),
    overflowY: 'auto',
    // Grey backdrop so the white section cards inside read as inset blocks
    // rather than white-on-white. Mirrors the /orders/:id pattern.
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // Horizontal scroll container for the tab row. Without this the four tabs'
  // intrinsic width stretches the layout on narrow screens; here the tabs
  // scroll sideways while the sections below keep the viewport width.
  tabScroller: {
    maxWidth: '100%',
    overflowX: 'auto',
    // Keep the tab strip on one line so overflow scrolls rather than wraps.
    flexShrink: 0,
    // Hide the scrollbar chrome — the tabs still scroll via touch/trackpad.
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  section: {
    ...shorthands.padding('20px', '24px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground1,
  },
  // Two-column Information layout: identity + companies-contacts on
  // the left, billing address on the right. Stacks on mobile.
  infoColumns: {
    display: 'flex',
    ...shorthands.gap('24px'),
    alignItems: 'flex-start',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
});

type TabValue = 'information' | 'locations' | 'orders' | 'payment-methods';

interface Order {
  id: string;
  number: number;
  status: string;
  starts_at: string;
  stops_at: string;
  created_at: string;
  /** Last *manual* edit by a user (drives "Last modified"); empty until the
   *  order's first in-app edit. Distinct from the sync-bumped updated_at. */
  last_edited_at: string;
  grand_total_in_cents: number;
  payment_status: string;
  /** When viewing a Company profile, orders are aggregated from the
   *  company itself + every linked Contact. This holds the display name
   *  of whoever was the Booqable buyer on the order ("Direct" if the
   *  company itself was the buyer). Undefined on Individual profiles. */
  placedByName?: string;
  placedById?: string;
}

interface CustomerProfileProps {
  customerId?: string;
}

const CustomerProfile: React.FC<CustomerProfileProps> = ({ customerId: propCustomerId }) => {
  const styles = useStyles();
  const { customerId: routeCustomerId } = useParams<{ customerId: string }>();
  const customerId = propCustomerId || routeCustomerId;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { debugMode } = useDebug();
  const isMobile = useIsMobile();

  // Reuse the customer drawer's edit form as the General tab body so we
  // get one source of truth for editable customer fields. The hook wants
  // an isOpen flag (it gates fetches and side effects on it) — pass true
  // since the page itself is the "open" surface. onClose is a no-op
  // because there's no drawer to dismiss.
  const customerForm = useCustomerForm({
    variant: 'edit',
    customerId: customerId || null,
    isOpen: true,
    onClose: () => {},
  });
  // Right-edge side panel — slot reserved for context that varies per
  // surface (e.g. activity feed, recent quick actions). Toggled via the
  // header button on the right side.

  // Tab survives refresh via URL hash (#orders, #locations). Lazy
  // initializer reads the hash once on mount; the effect below keeps the
  // hash in sync as the user clicks between tabs.
  const VALID_TABS: ReadonlyArray<string> = ['information', 'locations', 'orders', 'payment-methods'];
  const [selectedTab, setSelectedTab] = useState<TabValue>(() => {
    if (typeof window !== 'undefined') {
      const fromHash = window.location.hash.replace(/^#/, '');
      if (fromHash && VALID_TABS.includes(fromHash)) {
        return fromHash as TabValue;
      }
    }
    return 'information' as TabValue;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const next = `#${selectedTab}`;
    if (window.location.hash !== next) {
      // replaceState avoids polluting browser history with every tab click.
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${next}`);
    }
  }, [selectedTab]);
  // Identity comes from customerForm.bag — the form hook is the single
  // source of truth for editable customer fields. The page reads from the
  // bag for the breadcrumb, kind classification, and delete confirmation.
  const customerName = customerForm.bag.customerName;
  const customerKind = customerForm.bag.customerKind;
  const entityNoun =
    customerKind === 'contact' ? 'contact' :
    customerKind === 'company' ? 'company' :
    'customer';
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  // Conflict modal opens when DELETE returns 409 because the customer is a
  // Company with active Contacts. The user picks reassign-then-delete or
  // cascade delete from inside the modal.
  const [conflictModalOpen, setConflictModalOpen] = useState(false);

  // Fetch customer data using React Query with cache support
  const {
    data: customerResponse,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.customers.detail(customerId || ''),
    queryFn: async () => {
      if (!customerId) {
        throw new Error('No customer ID provided');
      }

      // /booqable/customers/<id> is dual-id-tolerant — accepts either TWM
      // uuid or Booqable id, resolves locally first. Single fetch path.
      const response = await apiFetch(`/booqable/customers/${customerId}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch customer: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✓ Full customer data fetched from API');
      return result;
    },
    enabled: !!customerId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const error = queryError ? (queryError instanceof Error ? queryError.message : 'Failed to fetch customer') : null;
  const hasPortalAccount: boolean = customerResponse?.data?.attributes?.has_portal_account ?? false;

  // If the user landed on a tab that's not valid for this kind (e.g.
  // /customers/<contact-id>#orders), snap back to Information. Driven by
  // bag.customerKind so we react to whatever kind the form hook computed.
  useEffect(() => {
    const allowed = customerKind === 'contact'
      ? new Set(['information'])
      : new Set(['information', 'locations', 'orders', 'payment-methods']);
    if (!allowed.has(selectedTab as string)) {
      setSelectedTab('information');
    }
  }, [customerKind, selectedTab]);

  // Orders for any non-contact profile. A Contact never owns orders (by
  // design — orders always attach to the Company), so there's no per-contact
  // roll-up to fetch: the company's own orders are the complete set. The
  // linked-contacts list itself is loaded by useCustomerForm for the
  // ContactsSection UI, so the page doesn't fetch it separately.
  const { data: orders = [], isLoading: ordersLoading, error: ordersQueryError } = useQuery<Order[]>({
    queryKey: queryKeys.customers.orders(customerId || ''),
    queryFn: async () => {
      const mapOrder = (order: any, placedByName?: string, placedById?: string): Order => {
        const attrs = order.attributes || {};
        return {
          id: order.id,
          number: attrs.number || 0,
          status: attrs.status || 'unknown',
          starts_at: attrs.starts_at || '',
          stops_at: attrs.stops_at || '',
          created_at: attrs.created_at || '',
          last_edited_at: attrs.last_edited_at || '',
          // Show what the customer pays (tax-inclusive). Booqable's
          // grand_total_in_cents is pre-tax and frequently null on the mirror;
          // grand_total_with_tax_in_cents is the charged total. Mirrors the
          // fallback used everywhere else (DocumentDetailsPane, app.py).
          grand_total_in_cents:
            attrs.grand_total_with_tax_in_cents ?? attrs.grand_total_in_cents ?? 0,
          payment_status: attrs.payment_status || 'unknown',
          placedByName,
          placedById,
        };
      };

      const ownRes = await apiFetch(`/customers/${customerId}/orders`);
      if (!ownRes.ok) throw new Error(`Failed to fetch orders: ${ownRes.statusText}`);
      const ownJson = await ownRes.json();
      return (ownJson.data || [])
        .map((o: any) =>
          mapOrder(
            o,
            customerKind === 'company' ? 'Direct' : undefined,
            customerKind === 'company' ? customerId : undefined,
          ),
        )
        .sort((a: Order, b: Order) =>
          (b.starts_at || '').localeCompare(a.starts_at || ''),
        );
    },
    enabled: !!customerId && customerKind !== 'contact',
    staleTime: 60 * 1000,
  });

  const ordersError = ordersQueryError instanceof Error ? ordersQueryError.message : null;

  if (loading) {
    return <CustomerProfileSkeleton />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#fef0f0',
          border: '1px solid #fcd4d4',
          borderRadius: '4px',
          color: '#c42b1c',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.workspaceCard}>
      {/* Pilot of the unified-surface treatment: the global Header is
          rendered here as the first row of the workspace card instead of
          floating above. Layout.tsx suppresses its usual `.page-header`
          slot for /customers/:id so it doesn't render twice. The
          customTitle slot carries the breadcrumb (Customers > [icon]
          [Name]) so this surface owns its own page identity. */}
      <Header
        inline
        stackTitleOnMobile
        customActions={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              icon={<Add24Regular />}
              appearance="primary"
              disabled={!customerId}
              onClick={() => {
                if (!customerId) return;
                navigate(`/orders/new?customer=${encodeURIComponent(customerId)}`);
              }}
            >
              Add Order
            </Button>
          </div>
        }
        customTitle={
          <>
            {/* Match the Customers list page title styling — fontSizeBase500
                (20px) + semibold so the breadcrumb reads at the same
                visual weight as "Customers" does on the list surface. */}
            <Link
              onClick={(e) => { e.preventDefault(); navigate('/customers'); }}
              href="/customers"
              style={{
                fontSize: tokens.fontSizeBase500,
                fontWeight: tokens.fontWeightSemibold,
                lineHeight: tokens.lineHeightBase300,
                color: tokens.colorNeutralForeground1,
                // Keep the breadcrumb root fixed so only the name truncates.
                flexShrink: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Customers
            </Link>
            <ChevronRight16Regular style={{ color: tokens.colorNeutralForeground3 }} />
            {/* Entity-kind avatar — same icon set as the customers list
                table rows so the visual mapping is consistent. Hidden on
                mobile to keep the title row compact. */}
            {!isMobile && (
              <Avatar
                icon={
                  customerKind === 'company' ? <Building20Filled />
                  : customerKind === 'contact' ? <PersonCall20Filled />
                  : <Person20Filled />
                }
                color="neutral"
                size={24}
                aria-label={
                  customerKind === 'company' ? 'Company'
                  : customerKind === 'contact' ? 'Contact'
                  : 'Individual'
                }
              />
            )}
            <span style={{
              fontSize: tokens.fontSizeBase500,
              fontWeight: tokens.fontWeightSemibold,
              lineHeight: tokens.lineHeightBase300,
              color: tokens.colorNeutralForeground1,
              // Truncate a long name with an ellipsis instead of wrapping to
              // a second line. minWidth:0 lets it shrink inside the flex row.
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {customerName || 'Loading…'}
            </span>
            {/* Same autosave indicator the drawer shows next to its
                title — Saving / Saved / Unsaved / Save failed. Renders
                nothing when status is 'idle'. */}
            <SaveStatusBadge status={customerForm.saveStatus} />
            {/* Indicators that previously lived in the hero band — kept
                here so the breadcrumb still surfaces portal-account state
                and the debug-mode customer ID copy. (The linked-contacts
                count is omitted — contacts are listed on the page itself.) */}
            {hasPortalAccount && (
              <Tooltip content="Has portal account" relationship="label">
                <PersonAccounts20Regular style={{ color: tokens.colorNeutralForeground3 }} />
              </Tooltip>
            )}
            {debugMode && customerId && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                <span style={{ fontSize: '14px', fontFamily: 'monospace', color: tokens.colorNeutralForeground2 }}>
                  {customerId}
                </span>
                <Tooltip content="Copy ID" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Copy16Regular />}
                    onClick={() => navigator.clipboard.writeText(customerId)}
                  />
                </Tooltip>
              </span>
            )}
          </>
        }
      />

      <div className={styles.bodyWithPanel}>
      <div className={styles.workspaceBody}>

        {/* Tabs split the non-contact profile into three views since the
            single-page layout had grown too dense:
              - Information: form sections (identity, address, contacts)
              - Locations: locations table
              - Orders: full-width orders table
            Contacts skip the tabs and render their slim identity view. */}
        {customerKind !== 'contact' && (
          <div className={styles.tabScroller}>
            <TabList
              selectedValue={selectedTab}
              onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
              style={{ marginTop: '0' }}
            >
              <Tab value="information">Information</Tab>
              <Tab value="locations">Locations</Tab>
              <Tab value="orders">Orders</Tab>
              <Tab value="payment-methods">Payment Methods</Tab>
            </TabList>
          </div>
        )}

        {customerKind === 'contact' && (
          <div style={{
            width: '100%',
            paddingTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {/* Editable identity for Contacts. Billing / orders / documents /
                payment methods still roll up to the parent Company; only the
                identity bits (name / email / phone / company link) live on
                the contact row. */}
            {customerForm.headerContent}
            <div className={styles.infoColumns}>
              <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className={styles.section}>
                  <GeneralSection form={customerForm.bag} showIcon={false} />
                </div>
              </div>
            </div>
            {/* Delete at the very bottom of the sections. */}
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                appearance="subtle"
                icon={<Delete24Regular />}
                onClick={() => setDeleteModalOpen(true)}
                style={{ color: tokens.colorPaletteRedForeground1 }}
              >
                {`Delete ${entityNoun}`}
              </Button>
            </div>
          </div>
        )}

        {customerKind !== 'contact' && selectedTab === 'information' && (
          <div style={{
            width: '100%',
            paddingTop: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
            {customerForm.headerContent}
            <div className={styles.infoColumns}>
              {/* Left column: identity + (companies) linked contacts. */}
              <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className={styles.section}>
                  <GeneralSection form={customerForm.bag} showIcon={false} />
                </div>
                {customerKind === 'company' && (
                  <div className={styles.section}>
                    <ContactsSection form={customerForm.bag} showIcon={false} />
                  </div>
                )}
              </div>
              {/* Right column: billing address + tags metadata. Tags sits as a
                  half-width column matching General rather than spanning full
                  width below the form. */}
              <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className={styles.section}>
                  <AddressSection form={customerForm.bag} showIcon={false} />
                </div>
                <div className={styles.section}>
                  <TagsSection form={customerForm.bag} showIcon={false} />
                </div>
              </div>
            </div>
            {/* Delete sits below both columns so it lands at the very bottom
                of the sections regardless of how the columns stack. */}
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Button
                appearance="subtle"
                icon={<Delete24Regular />}
                onClick={() => setDeleteModalOpen(true)}
                style={{ color: tokens.colorPaletteRedForeground1 }}
              >
                {`Delete ${entityNoun}`}
              </Button>
            </div>
          </div>
        )}

        {customerKind !== 'contact' && selectedTab === 'locations' && (
          <div style={{
            width: '100%',
            paddingTop: '16px',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div
              className={styles.section}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <CustomerLocationsPanel customerId={customerId} />
            </div>
          </div>
        )}

        {customerKind !== 'contact' && selectedTab === 'orders' && (
          <div style={{
            width: '100%',
            paddingTop: '16px',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
            {/* Title hidden — the active tab already labels this view. */}
            <div
              className={styles.section}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              <OrdersPanel
                orders={orders}
                loading={ordersLoading}
                error={ordersError}
                customerId={customerId}
                customerKind={customerKind}
                showTitle={false}
              />
            </div>
          </div>
        )}

        {customerKind !== 'contact' && selectedTab === 'payment-methods' && (
          <div style={{
            width: '100%',
            paddingTop: '16px',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div
              className={styles.section}
              style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
            >
              {customerId && <CustomerPaymentMethodsPanel customerId={customerId} />}
            </div>
          </div>
        )}


      </div>
      </div>

      {/* Delete Customer Modal */}
      <ConfirmationDialog
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={async () => {
          if (!customerId) return;
          try {
            await deleteCustomer(customerId);
            setDeleteModalOpen(false);
            await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
            navigate('/customers');
          } catch (err) {
            if (err instanceof CompanyHasContactsError) {
              // Backend signals "company has contacts" — defer to the
              // conflict modal so the user can reassign or cascade.
              setDeleteModalOpen(false);
              setConflictModalOpen(true);
              return;
            }
            console.error('Failed to delete customer:', err);
            alert(`Failed to delete customer: ${err instanceof Error ? err.message : String(err)}`);
          }
        }}
        title={`Delete ${entityNoun}`}
        message={`Are you sure you want to delete ${customerName || `this ${entityNoun}`}?`}
        subtext="This cannot be undone"
        variant="danger"
      />

      {customerId && (
        <CompanyDeleteConflictModal
          isOpen={conflictModalOpen}
          onClose={() => setConflictModalOpen(false)}
          companyId={customerId}
          companyName={customerName || 'this company'}
          onResolved={async () => {
            setConflictModalOpen(false);
            await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
            navigate('/customers');
          }}
        />
      )}

    </div>
  );
};

export default CustomerProfile;
